import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import AnimateHeight, { Height } from 'react-animate-height';
import { IconMenu02, IconX } from '@pentabd/icons';

import useMediaQuery, { SCREEN_SIZES } from '../../hooks/useMediaQuery';
import Text from '../text/Text';
import MenuItem from './MenuItem';

export interface SubItems {
  leftSubMenus?: {
    name: string;
    url: string;
    icon?: ReactNode;
    hide?: boolean;
  }[];
  rightSubComponents?: {
    name: string;
    component: ReactNode;
    hide?: boolean;
  }[];
}
export interface TopBarProps {
  toggleFrom?: 'Top' | 'Side';
  brand: {
    icon?: ReactNode;
    name?: string;
    url?: string;
  };
  leftNavMenu?: {
    icon?: ReactNode;
    name: string;
    child: string | SubItems;
    hide?: boolean;
  }[];
  searchMainMenu?: ReactNode;
  rightNavMenus?: {
    component: ReactNode;
    hide?: boolean;
  }[];
  userDetails: {
    component: ReactNode;
    name: string;
    email?: string;
  };
  bgColor?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link: any;
  dropdownMenu?: boolean;
}

const TopBar = ({
  toggleFrom = 'Top',
  brand,
  leftNavMenu,
  rightNavMenus,
  searchMainMenu,
  userDetails,
  dropdownMenu,
  bgColor = 'bg-white',
  Link,
}: TopBarProps) => {
  const isMobile = useMediaQuery(`(max-width: ${SCREEN_SIZES.SM})`);

  const [height, setHeight] = useState<Height | number>(0);
  const [openMenu, setOpenMenu] = useState(null);

  const toggleDropdown = (menuId: string) => {
    if (isMobile) {
      setOpenMenu(menuId);
    } else {
      if (openMenu === menuId) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menuId);
      }
    }
  };

  const showMobileMenu = () => {
    setHeight(height === 0 ? 'auto' : 0);
  };

  const TopBarMenu = () => {
    return (
      <div
        className={classNames('w-100', {
          'flex-column overflow-y-auto vh-100': isMobile,
          'd-flex justify-content-between align-items-center': !isMobile,
        })}
      >
        {isMobile && searchMainMenu && (
          <div className="my-12 px-8">{searchMainMenu}</div>
        )}
        <div
          className={classNames({
            'd-flex gap-3 align-items-center': !isMobile,
            'px-4': isMobile,
          })}
        >
          {leftNavMenu &&
            leftNavMenu.map((menu, index) => {
              return (
                <div key={index}>
                  {!menu.hide ? (
                    typeof menu.child === 'string' ? (
                      <Link to={menu.child} className="text-decoration-none">
                        <MenuItem
                          icon={menu.icon}
                          name={menu.name}
                          isMobile={isMobile}
                          Link={Link}
                        />
                      </Link>
                    ) : (
                      <MenuItem
                        icon={menu.icon}
                        name={menu.name}
                        submenu={menu.child}
                        isMobile={isMobile}
                        isOpen={openMenu === menu.name}
                        toggleDropdown={toggleDropdown}
                        Link={Link}
                        dropdown={dropdownMenu}
                      />
                    )
                  ) : null}
                </div>
              );
            })}
        </div>
        <div
          className={classNames({
            'd-flex gap-6 align-items-center': !isMobile,
            'px-4': isMobile,
          })}
        >
          {!isMobile && searchMainMenu}
          {rightNavMenus &&
            rightNavMenus.map((menu, index) => {
              return (
                <div key={index}>{!menu.hide ? menu.component : null}</div>
              );
            })}
          {userDetails && userDetails.component}
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames('top-0 right-0 w-100 d-flex scrollbar', bgColor, {
        'topbar topbar-desktop position-absolute px-16 align-items-center border-bottom flex-row gap-8':
          !isMobile,
        'topbar position-fixed flex-column pt-6 ': isMobile,
        'shadow-sm border-bottom pb-6': isMobile && height == 0,
      })}
    >
      <div
        className={classNames(
          'd-flex align-items-start justify-content-between',
          { 'ps-8 pe-4': isMobile },
        )}
      >
        <Link
          className="d-flex align-items-center text-decoration-none justify-content-center gap-5 text-nowrap"
          href={brand.url}
        >
          {brand.icon}
          {brand.name && (
            <Text weight="bold" color="dark">
              {brand.name}
            </Text>
          )}
        </Link>
        {isMobile && (
          <div
            id="mobile"
            onClick={showMobileMenu}
            className="pointer d-flex align-items-center"
          >
            {height === 0 ? <IconMenu02 size="24" /> : <IconX size="24" />}
          </div>
        )}
      </div>
      {!isMobile ? (
        <TopBarMenu />
      ) : toggleFrom == 'Top' ? (
        <AnimateHeight id="mobile" duration={500} height={height}>
          <TopBarMenu />
        </AnimateHeight>
      ) : (
        <div
          className={classNames('topbar-from-side', {
            'topbar-from-side-open': height === 'auto',
          })}
        >
          <TopBarMenu />
        </div>
      )}
    </div>
  );
};
export default TopBar;
